// Utility functions for lap download and upload
import Cookies from 'js-cookie';

export async function handleDownloadLaps({ lapIds, authFetch, BACKEND_URL, sessionUuid, setDownloadStatus }) {
  if (lapIds.length === 1) {
    // Single lap: download as JSON
    const lapId = lapIds[0];
    const res = await authFetch(`${BACKEND_URL}/laps/${lapId}`);
    if (res && res.ok) {
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = lapId;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    return;
  }
  // Multiple laps: use batch download endpoint with progress
  setDownloadStatus({ status: 'downloading', speed: null, eta: null, progress: 0 });
  try {
    const token = Cookies.get('session_token');
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BACKEND_URL}/laps/batch_download`);
      xhr.responseType = 'blob';
      xhr.setRequestHeader('Content-Type', 'application/json');
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      const progressBuffer = [];
      xhr.onprogress = (event) => {
        if (event.lengthComputable) {
          const now = performance.now();
          const loaded = event.loaded;
          progressBuffer.push({ time: now, loaded });
          while (progressBuffer.length > 1 && now - progressBuffer[0].time > 10000) {
            progressBuffer.shift();
          }
          let speed = null, eta = null;
          if (progressBuffer.length > 1) {
            const dt = (progressBuffer[progressBuffer.length - 1].time - progressBuffer[0].time) / 1000;
            const dBytes = progressBuffer[progressBuffer.length - 1].loaded - progressBuffer[0].loaded;
            speed = dt > 0 ? dBytes / dt : null;
            const remaining = event.total - loaded;
            eta = speed > 0 ? remaining / speed : null;
          }
          const percent = (loaded / event.total) * 100;
          setDownloadStatus({ status: 'downloading', speed, eta, progress: percent });
        }
      };
      xhr.onload = () => {
        setDownloadStatus({ status: 'done', speed: null, eta: 0, progress: 100 });
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = xhr.response;
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `laps_${sessionUuid || 'session'}.zip`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setTimeout(() => setDownloadStatus(null), 2000);
          resolve();
        } else {
          setDownloadStatus(null);
          alert('Batch download failed.');
          reject(new Error('Batch download failed'));
        }
      };
      xhr.onerror = () => {
        setDownloadStatus(null);
        alert('Batch download failed.');
        reject(new Error('Batch download failed'));
      };
      xhr.send(JSON.stringify(lapIds));
    });
  } catch (e) {
    setDownloadStatus(null);
    alert('Batch download failed: ' + e.message);
  }
}

export async function handleUploadLaps({ files, setUploadStatus, setUploadError, setShowUploadError, setUploadSuccess, setShowUploadSuccess, authFetch, BACKEND_URL, setLaps }) {
  setUploadStatus(null);
  if (files.length > 1) {
    // Batch upload
    const lapsPayload = [];
    let totalBytes = 0;
    for (const file of files) {
      if (!file.name.endsWith('.json')) {
        setUploadError(`File '${file.name}' is not a .json file.`);
        setShowUploadError(true);
        continue;
      }
      const text = await file.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        setUploadError(`File '${file.name}' is not valid JSON.`);
        setShowUploadError(true);
        continue;
      }
      let lap_id = file.name;
      if (!lap_id.endsWith('.json')) lap_id += '.json';
      if (!/^lap_\d{2}-\d{2}-\d{3}\.json$/.test(lap_id)) {
        setUploadError(`File name '${file.name}' does not match the required lap_XX-XX-XXX.json format.`);
        setShowUploadError(true);
        continue;
      }
      const payloadObj = Array.isArray(data)
        ? { lap_id: lap_id.replace('.json', ''), data }
        : { lap_id: lap_id.replace('.json', ''), data: data.data || data };
      lapsPayload.push(payloadObj);
      totalBytes += new Blob([JSON.stringify(payloadObj)]).size;
    }
    if (lapsPayload.length > 0) {
      const payloadStr = JSON.stringify(lapsPayload);
      totalBytes = new Blob([payloadStr]).size;
      setUploadStatus({ status: 'uploading', speed: null, eta: null, progress: 0 });
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/laps/batch`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        const token = Cookies.get('session_token');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        const progressBuffer = [];
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const now = performance.now();
            const loaded = event.loaded;
            progressBuffer.push({ time: now, loaded });
            while (progressBuffer.length > 1 && now - progressBuffer[0].time > 10000) {
              progressBuffer.shift();
            }
            let speed = null, eta = null;
            if (progressBuffer.length > 1) {
              const dt = (progressBuffer[progressBuffer.length - 1].time - progressBuffer[0].time) / 1000;
              const dBytes = progressBuffer[progressBuffer.length - 1].loaded - progressBuffer[0].loaded;
              speed = dt > 0 ? dBytes / dt : null;
              const remaining = event.total - loaded;
              eta = speed > 0 ? remaining / speed : null;
            }
            const percent = (loaded / event.total) * 100;
            setUploadStatus({ status: 'uploading', speed, eta, progress: percent });
          }
        };
        xhr.onload = async () => {
          setUploadStatus({ status: 'done', speed: null, eta: 0, progress: 100 });
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const results = JSON.parse(xhr.responseText);
              const errors = results.filter(r => r.status !== 'uploaded');
              if (errors.length > 0) {
                setUploadError(`Some laps failed: ${errors.map(e => e.lap_id || '').join(', ')}`);
                setShowUploadError(true);
              } else {
                setUploadSuccess('All laps uploaded successfully!');
                setShowUploadSuccess(true);
              }
              authFetch(`${BACKEND_URL}/laps/`)
                .then(res => res.ok ? res.json() : null)
                .then(data => { if (data) setLaps(data); });
            } catch {
              setUploadError('Batch upload failed (bad response).');
              setShowUploadError(true);
            }
          } else {
            setUploadError('Batch upload failed.');
            setShowUploadError(true);
          }
          setTimeout(() => setUploadStatus(null), 2000);
          resolve();
        };
        xhr.onerror = () => {
          setUploadError('Batch upload failed (network error).');
          setShowUploadError(true);
          setUploadStatus(null);
          reject();
        };
        xhr.send(payloadStr);
      });
    }
    return;
  }
  // Single file fallback
  for (const file of files) {
    try {
      if (!file.name.endsWith('.json')) {
        setUploadError(`File '${file.name}' is not a .json file.`);
        setShowUploadError(true);
        continue;
      }
      const text = await file.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        setUploadError(`File '${file.name}' is not valid JSON.`);
        setShowUploadError(true);
        continue;
      }
      let lap_id = file.name;
      if (!lap_id.endsWith('.json')) lap_id += '.json';
      if (!/^lap_\d{2}-\d{2}-\d{3}\.json$/.test(lap_id)) {
        setUploadError(`File name '${file.name}' does not match the required lap_XX-XX-XXX.json format.`);
        setShowUploadError(true);
        continue;
      }
      const payload = Array.isArray(data)
        ? { lap_id: lap_id.replace('.json', ''), data }
        : { lap_id: lap_id.replace('.json', ''), data: data.data || data };
      const payloadStr = JSON.stringify(payload);
      setUploadStatus({ status: 'uploading', speed: null, eta: null, progress: 0 });
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BACKEND_URL}/laps`);
        xhr.setRequestHeader('Content-Type', 'application/json');
        const token = Cookies.get('session_token');
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        const progressBuffer = [];
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const now = performance.now();
            const loaded = event.loaded;
            progressBuffer.push({ time: now, loaded });
            while (progressBuffer.length > 1 && now - progressBuffer[0].time > 10000) {
              progressBuffer.shift();
            }
            let speed = null, eta = null;
            if (progressBuffer.length > 1) {
              const dt = (progressBuffer[progressBuffer.length - 1].time - progressBuffer[0].time) / 1000;
              const dBytes = progressBuffer[progressBuffer.length - 1].loaded - progressBuffer[0].loaded;
              speed = dt > 0 ? dBytes / dt : null;
              const remaining = event.total - loaded;
              eta = speed > 0 ? remaining / speed : null;
            }
            const percent = (loaded / event.total) * 100;
            setUploadStatus({ status: 'uploading', speed, eta, progress: percent });
          }
        };
        xhr.onload = async () => {
          setUploadStatus({ status: 'done', speed: null, eta: 0, progress: 100 });
          if (xhr.status >= 200 && xhr.status < 300) {
            setUploadSuccess(`Lap '${file.name}' uploaded successfully!`);
            setShowUploadSuccess(true);
            authFetch(`${BACKEND_URL}/laps/`)
              .then(res => res.ok ? res.json() : null)
              .then(data => { if (data) setLaps(data); });
          } else {
            let msg = `Failed to upload '${file.name}'.`;
            try {
              const err = JSON.parse(xhr.responseText);
              if (err.detail) msg += ` ${err.detail}`;
            } catch {}
            setUploadError(msg);
            setShowUploadError(true);
          }
          setTimeout(() => setUploadStatus(null), 2000);
          resolve();
        };
        xhr.onerror = () => {
          setUploadError(`Failed to upload '${file.name}' (network error).`);
          setShowUploadError(true);
          setUploadStatus(null);
          reject();
        };
        xhr.send(payloadStr);
      });
    } catch (e) {
      setUploadError(`Unexpected error uploading '${file.name}': ${e.message}`);
      setShowUploadError(true);
    }
  }
}
